import {
  computeVendorOrderMetrics,
  resolvePriceFrom,
  resolvePriceTier,
  resolveTurnaroundBucket,
  resolveTurnaroundDays,
  resolveVendorAvailability,
  resolveVendorCapabilities,
  resolveVendorPositioning,
  type VendorAvailability,
  type VendorCapability,
  type VendorOrderMetrics,
  type VendorPriceTier,
  type VendorTurnaroundBucket,
} from "@nexus/contracts";
import type {
  MarketplaceOrder,
  TenantVendorConnection,
  Vendor,
  VendorPackage,
} from "../types/marketplace.types";

export const VENDOR_SORTS = [
  "recommended",
  "fastest",
  "cheapest",
  "recent",
] as const;

export type VendorSort = (typeof VENDOR_SORTS)[number];
export type VendorConnectionFilter = "connected" | "available";

export interface VendorCatalogFilters {
  query: string;
  connection: VendorConnectionFilter | null;
  capabilities: VendorCapability[];
  turnaround: VendorTurnaroundBucket | null;
  priceTier: VendorPriceTier | null;
  sort: VendorSort;
}

export interface VendorCatalogCardModel {
  vendor: Vendor;
  positioning: string | null;
  capabilities: VendorCapability[];
  priceFrom: number | null;
  currency: string | null;
  turnaroundDays: number | null;
  turnaroundBucket: VendorTurnaroundBucket | null;
  priceTier: VendorPriceTier | null;
  availability: VendorAvailability;
  metrics: VendorOrderMetrics;
  connection: TenantVendorConnection | null;
  isConnected: boolean;
  isRecent: boolean;
}

export function emptyVendorCatalogFilters(): VendorCatalogFilters {
  return {
    query: "",
    connection: null,
    capabilities: [],
    turnaround: null,
    priceTier: null,
    sort: "recommended",
  };
}

function activePackages(vendor: Vendor): VendorPackage[] {
  return vendor.packages.filter((pkg) => pkg.isActive);
}

export function buildVendorCatalogCard(
  vendor: Vendor,
  connections: readonly TenantVendorConnection[],
  orders: readonly MarketplaceOrder[],
  recentVendorIds: readonly string[] = [],
): VendorCatalogCardModel {
  const packages = activePackages(vendor);
  const priceFrom = resolvePriceFrom(packages);
  const pricedPackage = packages.find((pkg) => pkg.price === priceFrom);
  const turnaroundDays = resolveTurnaroundDays({
    defaultDeliveryDays: vendor.defaultDeliveryDays,
    packages,
  });
  const connection =
    connections.find((item) => item.vendorId === vendor.id) ?? null;

  return {
    vendor,
    positioning: resolveVendorPositioning({
      positioning: null,
      description: vendor.description,
    }),
    capabilities: resolveVendorCapabilities({
      name: vendor.name,
      description: vendor.description,
      packages,
      capabilities: vendor.capabilities,
    }),
    priceFrom,
    currency: pricedPackage?.currency ?? packages.find((pkg) => pkg.currency)?.currency ?? null,
    turnaroundDays,
    turnaroundBucket: resolveTurnaroundBucket(turnaroundDays),
    priceTier: resolvePriceTier(priceFrom),
    availability: resolveVendorAvailability({
      isActive: vendor.isActive,
      canReceiveOrders: vendor.canReceiveOrders,
    }),
    metrics: computeVendorOrderMetrics(
      vendor.id,
      orders.map((order) => ({
        vendorId: order.vendorId,
        fulfillmentStatus: order.fulfillmentStatus,
        updatedAt: order.sentAt ?? order.createdAt,
        etaDate: order.etaDate,
        scripts: order.scripts.map((script) => ({
          fulfillmentStatus: script.fulfillmentStatus,
          revisionNote: script.revisionNote,
          expectedDeliveryDate: script.expectedDeliveryDate,
        })),
      })),
    ),
    connection,
    isConnected: connection != null,
    isRecent: recentVendorIds.includes(vendor.id),
  };
}

export function buildVendorCatalog(
  vendors: readonly Vendor[],
  connections: readonly TenantVendorConnection[],
  orders: readonly MarketplaceOrder[],
  recentVendorIds: readonly string[] = [],
): VendorCatalogCardModel[] {
  return vendors.map((vendor) =>
    buildVendorCatalogCard(vendor, connections, orders, recentVendorIds),
  );
}

function matchesQuery(card: VendorCatalogCardModel, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  const haystack = [
    card.vendor.name,
    card.vendor.slug,
    card.positioning,
    card.vendor.description,
    ...card.capabilities,
    ...card.vendor.packages.map((pkg) => pkg.name),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(needle);
}

export function filterVendorCatalog(
  cards: readonly VendorCatalogCardModel[],
  filters: VendorCatalogFilters,
): VendorCatalogCardModel[] {
  return cards.filter((card) => {
    if (!matchesQuery(card, filters.query)) return false;
    if (filters.connection === "connected" && !card.isConnected) return false;
    if (filters.connection === "available" && card.isConnected) return false;
    if (
      filters.capabilities.length > 0 &&
      !filters.capabilities.every((capability) =>
        card.capabilities.includes(capability),
      )
    ) {
      return false;
    }
    if (filters.turnaround && card.turnaroundBucket !== filters.turnaround) {
      return false;
    }
    if (filters.priceTier && card.priceTier !== filters.priceTier) {
      return false;
    }
    return true;
  });
}

function compareRecommended(a: VendorCatalogCardModel, b: VendorCatalogCardModel): number {
  if (a.isConnected !== b.isConnected) return a.isConnected ? -1 : 1;
  if (a.isRecent !== b.isRecent) return a.isRecent ? -1 : 1;
  const aOnTime = a.metrics.onTimeRate ?? -1;
  const bOnTime = b.metrics.onTimeRate ?? -1;
  if (aOnTime !== bOnTime) return bOnTime - aOnTime;
  return a.vendor.name.localeCompare(b.vendor.name);
}

export function sortVendorCatalog(
  cards: readonly VendorCatalogCardModel[],
  sort: VendorSort,
): VendorCatalogCardModel[] {
  const copy = [...cards];
  copy.sort((a, b) => {
    if (sort === "fastest") {
      const aDays = a.turnaroundDays ?? Number.POSITIVE_INFINITY;
      const bDays = b.turnaroundDays ?? Number.POSITIVE_INFINITY;
      if (aDays !== bDays) return aDays - bDays;
    }
    if (sort === "cheapest") {
      const aPrice = a.priceFrom ?? Number.POSITIVE_INFINITY;
      const bPrice = b.priceFrom ?? Number.POSITIVE_INFINITY;
      if (aPrice !== bPrice) return aPrice - bPrice;
    }
    if (sort === "recent") {
      const aUsed = a.metrics.lastUsedAt ?? "";
      const bUsed = b.metrics.lastUsedAt ?? "";
      if (aUsed !== bUsed) return bUsed.localeCompare(aUsed);
    }
    return compareRecommended(a, b);
  });
  return copy;
}

export function presentVendorCatalog(
  vendors: readonly Vendor[],
  connections: readonly TenantVendorConnection[],
  orders: readonly MarketplaceOrder[],
  filters: VendorCatalogFilters,
  recentVendorIds: readonly string[] = [],
): VendorCatalogCardModel[] {
  return sortVendorCatalog(
    filterVendorCatalog(
      buildVendorCatalog(vendors, connections, orders, recentVendorIds),
      filters,
    ),
    filters.sort,
  );
}

export function formatVendorPriceFrom(
  amount: number,
  currency: string | null,
): string {
  if (currency) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency,
        maximumFractionDigits: 0,
      }).format(amount);
    } catch {
      return `${currency} ${amount.toLocaleString()}`;
    }
  }
  return amount.toLocaleString();
}
